import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/api/client";
import { User, Clock, Eye, ArrowLeft, Loader2, Sparkles, BookOpen } from "lucide-react";

export default function PublicAuthor() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/authors/${slug}`);
        setData(data);
        document.title = `${data.author.name} · Affiliate Terminal`;
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    return () => { document.title = "Affiliate Terminal"; };
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-500 flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading profile…
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-2xl text-sky-100">Author not found.</p>
        <Link to="/browse" className="mt-6 inline-flex items-center gap-2 text-sky-300"><ArrowLeft className="w-4 h-4" /> Back to browse</Link>
      </div>
    );
  }

  const { author, articles, stats } = data;
  const initials = author.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16 fade-up">
      <Link to="/browse" className="text-xs text-neutral-500 hover:text-sky-300 mb-6 flex items-center gap-1 font-mono uppercase tracking-widest">
        <ArrowLeft className="w-3 h-3" /> Back to browse
      </Link>

      {/* Header */}
      <div className="flex items-start gap-6 mb-10 pb-10 border-b border-slate-800">
        <div className="w-20 h-20 rounded-full border border-sky-400/30 bg-sky-400/5 flex items-center justify-center flex-shrink-0">
          <span className="font-serif text-3xl text-sky-300">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Creator profile</p>
          <h1 data-testid="author-name" className="font-serif text-4xl lg:text-5xl text-sky-100 tracking-tight">{author.name}</h1>
          {author.joined_at && (
            <p className="mt-2 text-neutral-500 text-xs font-mono uppercase tracking-widest">
              Joined {new Date(author.joined_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-6 text-sm">
            <span data-testid="author-stat-articles" className="text-neutral-400">
              <span className="font-numeric text-2xl font-serif text-sky-300 mr-2">{stats.article_count}</span> published
            </span>
            <span data-testid="author-stat-reads" className="text-neutral-400">
              <span className="font-numeric text-2xl font-serif text-sky-300 mr-2">{stats.total_reads}</span> reads
            </span>
            <span data-testid="author-stat-niches" className="text-neutral-400">
              <span className="font-numeric text-2xl font-serif text-sky-300 mr-2">{stats.niches.length}</span> niches
            </span>
            <span className="text-neutral-400">
              <span className="font-numeric text-2xl font-serif text-sky-300 mr-2">{stats.languages.length}</span> languages
            </span>
          </div>
          {stats.niches.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {stats.niches.map((n) => (
                <span key={n} className="text-[10px] font-mono uppercase tracking-widest border border-sky-400/30 text-sky-300 px-2 py-1 rounded-full">
                  {n}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Articles */}
      {articles.length === 0 ? (
        <div className="border border-slate-800 rounded-xl p-16 text-center">
          <p className="font-serif text-xl text-sky-100">No public articles yet.</p>
          <p className="text-neutral-500 text-sm mt-2">This creator hasn't published anything public.</p>
        </div>
      ) : (
        <>
          <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            <BookOpen className="w-3 h-3 inline mr-1.5" /> All articles
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link
                key={a.id}
                data-testid={`author-article-${a.id}`}
                to={`/read/${a.slug || a.id}`}
                className="border border-slate-800 rounded-xl overflow-hidden hover:border-sky-400/40 transition group flex flex-col"
              >
                {a.hero_image_url ? (
                  <img
                    src={a.hero_image_url.startsWith("http") ? a.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${a.hero_image_url}`}
                    alt={a.title}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-sky-500/20 to-slate-900" />
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2 truncate">{a.niche}</p>
                  <h3 className="font-serif text-lg text-sky-100 group-hover:text-sky-300 transition line-clamp-2 leading-snug flex-1">{a.title}</h3>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800 text-[11px] font-mono text-neutral-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read_time} min</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {a.public_views || 0}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* CTA */}
      <div className="mt-16 border border-sky-500/25 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 to-transparent">
        <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2">— Start your own</p>
        <h3 className="font-serif text-2xl text-sky-100">Publish articles like {author.name} in <span className="italic text-sky-400">90 seconds.</span></h3>
        <Link to="/signup" className="mt-4 inline-flex items-center gap-2 bg-sky-400 text-slate-950 px-5 py-2.5 rounded-md font-medium">
          <Sparkles className="w-4 h-4" /> Try for free
        </Link>
      </div>
    </div>
  );
}
